import { useContext, useRef } from "react";
import type { ChangeEvent, ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { InlineEdit, displayValue } from "@/components/design/InlineEdit";
import { Select } from "@/components/design/Select";
import { Switch } from "@/components/design/Switch";
import type { FieldOption } from "@/lib/field-util";
import { AVATAR_PLACEHOLDER_STYLE, avatarInitials } from "@/lib/avatar";
import { EditContext, ShareEditContext } from "./context";
import { ProfileMergeField } from "@/components/save/ProfileMergeField";
import { useProfileMergeStore, detailDiffFor } from "@/stores/profile-merge-store";

// A field hosting an import-review card takes the FULL grid row. The card is a
// whole editing surface — compared pair plus the keep/use button pair — and the
// two-column business-card grid leaves a cell only ~160px wide at 1280x720,
// where the buttons no longer fit beside each other (spark#174). Only from `@sm`
// up: that is where the host grids switch to two columns, and col-span-2 in a
// one-column grid would invent a second column.
const REVIEW_SPAN = "@sm:col-span-2";

export function AvatarUpload({
  avatarUrl,
  name,
  editable,
  hasAvatar,
  onSelectFile,
  onRemove,
}: {
  avatarUrl: string | null;
  name: string;
  editable: boolean;
  hasAvatar: boolean;
  onSelectFile: (file: File) => void;
  onRemove: () => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const intl = useIntl();

  function handleFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) onSelectFile(file);
  }

  return (
    <div className="flex flex-col items-center gap-2">
      <button
        type="button"
        onClick={editable ? () => inputRef.current?.click() : undefined}
        disabled={!editable}
        title={
          editable
            ? intl.formatMessage({ id: "profile.fields.changePhoto", defaultMessage: "Change photo" })
            : undefined
        }
        className={`group/avatar relative flex h-32 w-32 items-center justify-center overflow-hidden rounded-full font-serif text-4xl ring-1 ring-ink/10 ${editable ? "cursor-pointer" : "cursor-default"}`}
        style={avatarUrl ? undefined : AVATAR_PLACEHOLDER_STYLE}
      >
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={name || intl.formatMessage({ id: "profile.fields.avatarAlt", defaultMessage: "Avatar" })}
            className="h-full w-full object-cover"
          />
        ) : (
          avatarInitials(name)
        )}
        {editable ? (
          <span className="absolute inset-0 flex items-center justify-center rounded-full bg-ink/40 text-[11px] tracking-[0.05em] text-on-ink opacity-0 transition group-hover/avatar:opacity-100">
            <FormattedMessage id="profile.fields.changePhoto" defaultMessage="Change photo" />
          </span>
        ) : null}
      </button>
      {editable && hasAvatar ? (
        <button
          type="button"
          onClick={onRemove}
          className="text-[11px] tracking-[0.05em] text-ink/45 transition hover:text-accent"
        >
          <FormattedMessage id="profile.fields.removePhoto" defaultMessage="Remove photo" />
        </button>
      ) : null}
      {editable ? (
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFile}
        />
      ) : null}
    </div>
  );
}

function FieldHeader({
  label,
  shared,
  onSharedChange,
  className = "",
  shareId,
}: {
  label: string;
  shared: boolean;
  onSharedChange?: (next: boolean) => void;
  className?: string;
  shareId?: string;
}) {
  return (
    <div className={`flex items-center justify-between gap-2 ${className}`}>
      <div className="field-label">{label}</div>
      <ShareToggle shared={shared} onChange={onSharedChange} id={shareId} />
    </div>
  );
}

export function ShareToggle({
  shared,
  onChange,
  id,
}: {
  shared: boolean;
  onChange?: (next: boolean) => void;
  id?: string;
}) {
  const intl = useIntl();
  const { mounted, exiting } = useContext(ShareEditContext);
  if (!onChange || !mounted) return null;
  return (
    <Switch
      id={id}
      checked={shared}
      onChange={onChange}
      ariaLabel={intl.formatMessage(
        shared
          ? { id: "common.share.ariaActive", defaultMessage: "Sharing active" }
          : { id: "common.share.ariaInactive", defaultMessage: "Not sharing" },
      )}
      label={{
        on: intl.formatMessage({ id: "common.share.on", defaultMessage: "Share" }),
        off: intl.formatMessage({ id: "common.share.off", defaultMessage: "Private" }),
      }}
      exiting={exiting}
    />
  );
}

// Forces the share toggle visible regardless of the global "Sichtbarkeit
// bearbeiten" mode. Used inside an entry's edit panel: editing a single entry
// should always expose its share control (the top button only toggles them
// all at once for convenience).
export function ForcedShare({ children }: { children: ReactNode }) {
  return (
    <ShareEditContext.Provider value={{ mounted: true, exiting: false }}>
      <div className="flex justify-end">{children}</div>
    </ShareEditContext.Provider>
  );
}

export function BasicField({
  label,
  value,
  onChange,
  readOnly = false,
  type = "text",
  min,
  max,
  shared = false,
  onSharedChange,
  suffix,
  id,
  mergeKey,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  readOnly?: boolean;
  type?: "text" | "date" | "number";
  min?: number;
  max?: number;
  // Omit both to render a field with no share toggle (e.g. jobTitle, which has
  // no shared* attribute in genesis). ShareToggle hides itself without onChange.
  shared?: boolean;
  onSharedChange?: (next: boolean) => void;
  suffix?: string | null;
  id?: string;
  // When set, an in-line LinkedIn merge affordance for this genesis field key
  // (e.g. "bio", "profession") renders under the field when an import preview is
  // active. Renders nothing otherwise, so it is safe to pass unconditionally.
  mergeKey?: string;
}) {
  const editable = useContext(EditContext);
  // Floating-label pattern: while the field is empty the label sits inside
  // the field as a muted placeholder; once a value is present the label moves
  // above as the small `.field-label` eyebrow and the value takes the
  // prominent position.
  const hasValue = Boolean(value);
  const shareId = id ? `${id}-share` : undefined;
  // While LinkedIn proposes a change, the field is locked and the editable
  // compared pair is rendered below it. The field itself intentionally keeps the
  // same border/outline treatment as every other profile field. The review is
  // an editing surface: on a non-editable profile (viewing another person while
  // one's own preview is active) no merge affordance may appear.
  const mergeDiff = useProfileMergeStore((s) =>
    mergeKey && editable ? detailDiffFor(s.preview, mergeKey) : null,
  );
  // While LinkedIn proposes a change for this field, the ORIGINAL value is
  // locked: it keeps showing what is saved in the system, and all editing
  // happens on the compared pair in the merge card below. (It would also drift
  // the pristine value the save is diffed against.)
  const showStatic = readOnly || !editable || mergeDiff != null;
  return (
    <div className={mergeDiff ? REVIEW_SPAN : undefined}>
      {hasValue ? (
        <FieldHeader label={label} shared={shared} onSharedChange={onSharedChange} shareId={shareId} />
      ) : null}
      <div className={`flex items-baseline gap-2 ${hasValue ? "mt-0.5" : ""}`}>
        {showStatic ? (
          hasValue ? (
            <div className="field-value text-ink/80">{displayValue(value, type)}</div>
          ) : (
            <div className="field-value min-w-0 flex-1 text-ink/30">{label}</div>
          )
        ) : (
          <InlineEdit
            id={id}
            value={value}
            onChange={onChange}
            type={type}
            min={min}
            max={max}
            placeholder={label}
            className="field-value min-w-0 flex-1"
            inputClassName="field-value"
          />
        )}
        {/* No eyebrow header in the empty state, so the toggle rides the
            placeholder line - ml-auto keeps it right-aligned, matching the
            header toggle of filled fields. */}
        {!hasValue ? (
          <div className="ml-auto shrink-0">
            <ShareToggle shared={shared} onChange={onSharedChange} id={shareId} />
          </div>
        ) : null}
        {suffix ? (
          <span className="field-meta whitespace-nowrap text-ink/45">{suffix}</span>
        ) : null}
      </div>
      {mergeKey && editable ? <ProfileMergeField fieldKey={mergeKey} type={type} /> : null}
    </div>
  );
}

export function SelectField({
  label,
  value,
  onChange,
  options,
  shared,
  onSharedChange,
  id,
  mergeKey,
  mergeFormat,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: FieldOption[];
  shared: boolean;
  onSharedChange?: (next: boolean) => void;
  id?: string;
  // A reference field (gender, marital status, country) whose stored value is an
  // id. During an import merge the field locks and a read-only merge card renders
  // below, showing the label via `mergeFormat` and committing the id.
  mergeKey?: string;
  mergeFormat?: (value: string) => string;
}) {
  const editable = useContext(EditContext);
  const hasValue = Boolean(value);
  const shareId = id ? `${id}-share` : undefined;
  const mergeDiff = useProfileMergeStore((s) =>
    mergeKey && editable ? detailDiffFor(s.preview, mergeKey) : null,
  );
  // While the import proposes a change the select is locked (like BasicField):
  // the original keeps showing the saved value and the choice happens in the card.
  const selectEditable = editable && mergeDiff == null;
  return (
    <div className={mergeDiff ? REVIEW_SPAN : undefined}>
      {hasValue ? (
        <FieldHeader label={label} shared={shared} onSharedChange={onSharedChange} shareId={shareId} />
      ) : null}
      <div className={`flex items-baseline gap-2 ${hasValue ? "mt-0.5" : ""}`}>
        <div className="min-w-0 flex-1">
          <Select
            id={id}
            value={value}
            onChange={onChange}
            options={options}
            editable={selectEditable}
            placeholder={label}
            variant="plain"
          />
        </div>
        {!hasValue ? (
          <div className="ml-auto shrink-0">
            <ShareToggle shared={shared} onChange={onSharedChange} id={shareId} />
          </div>
        ) : null}
      </div>
      {mergeKey && editable ? <ProfileMergeField fieldKey={mergeKey} editable={false} format={mergeFormat} /> : null}
    </div>
  );
}

export function MultiCountryField({
  label,
  value,
  onChange,
  options,
  shared,
  onSharedChange,
  id,
  mergeKey,
  mergeFormat,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: FieldOption[];
  shared: boolean;
  onSharedChange: (next: boolean) => void;
  id?: string;
  // A multi-value id field (citizenship). During an import merge the chips lock
  // and a read-only merge card renders below (the incoming side is the merged
  // id-list), showing labels via `mergeFormat` and committing the ids.
  mergeKey?: string;
  mergeFormat?: (value: string) => string;
}) {
  const editable = useContext(EditContext);
  const intl = useIntl();
  const mergeDiff = useProfileMergeStore((s) =>
    mergeKey && editable ? detailDiffFor(s.preview, mergeKey) : null,
  );
  // While the import proposes a change the chips are locked; the card drives it.
  const chipsEditable = editable && mergeDiff == null;
  const ids = value ? String(value).split(",").filter(Boolean) : [];
  const labelFor = (cid: string) => options.find((o) => o.value === cid)?.label || cid;
  const remaining = options.filter((o) => !ids.includes(o.value));
  const add = (cid: string) => {
    if (cid && !ids.includes(cid)) onChange([...ids, cid].join(","));
  };
  const remove = (cid: string) => onChange(ids.filter((x) => x !== cid).join(","));
  return (
    <div id={id} className={mergeDiff ? REVIEW_SPAN : undefined}>
      <FieldHeader
        label={label}
        shared={shared}
        onSharedChange={onSharedChange}
        className="mb-1"
        shareId={id ? `${id}-share` : undefined}
      />
      <div className="flex flex-wrap items-center gap-2">
        {ids.length === 0 && !chipsEditable ? (
          <span className="field-value text-ink/30">–</span>
        ) : null}
        {ids.map((cid) => (
          <span
            key={cid}
            className="field-meta inline-flex items-center gap-1 rounded-full bg-surface px-2.5 py-1 ring-1 ring-ink/10"
          >
            {labelFor(cid)}
            {chipsEditable ? (
              <button
                type="button"
                onClick={() => remove(cid)}
                className="text-ink/40 hover:text-accent"
                aria-label={intl.formatMessage({ id: "common.remove", defaultMessage: "Remove" })}
              >
                ✕
              </button>
            ) : null}
          </span>
        ))}
        {chipsEditable && remaining.length > 0 ? (
          <Select
            value=""
            onChange={add}
            options={remaining}
            placeholder={intl.formatMessage({ id: "profile.fields.addCountry", defaultMessage: "+ Country" })}
            variant="plain"
          />
        ) : null}
      </div>
      {mergeKey && editable ? <ProfileMergeField fieldKey={mergeKey} editable={false} format={mergeFormat} /> : null}
    </div>
  );
}
